import { FC, useMemo } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import './card-details.css'

// COMPONENTS
import { Column } from '@components/templates/column'
import GroupCard from '@components/organisims/group-card'
import Button from '@components/atoms/button'

// TYPES
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import { getGroupStatus } from '@utils/temp-helpers'
import { parseTeraUserData } from '@schemas'
import { isNonEmptyString } from '@utils/type-checkers'
import { useGroupLoaderData } from '..'


interface Props { }

const GroupCardDetailsScreen: FC<Props> = () => {
    const { isSignedIn, user } = useUser()
    const group = useGroupLoaderData()
    const { groupStatus } = getGroupStatus(group)
    const navigate = useNavigate()

    const billingAddress = useMemo(() => parseTeraUserData(user?.publicMetadata?.tera).address, [user?.publicMetadata.tera])
    const isOrganizer = isSignedIn ? group.organizer?.id === user.id : false
    const isMerchant = !!user?.publicMetadata.merchant && !!group.merchant && user?.publicMetadata.merchant?.id === group.merchant?.id
    const canView = group.type === 'merchant' ? isMerchant : isOrganizer

    const { userPledges } = useMemo(() => [...(group.pledges || [])].reduce<{ userPledges: Pledge[] }>((prev, pledge) => {
        if (isSignedIn && isNonEmptyString(user?.id)) {
            if (pledge.user?.id === user.id) return { ...prev, userPledges: [...prev.userPledges, pledge] };
            else return { ...prev }
        } else return prev
    }, { userPledges: [] }), [isSignedIn, user?.id, group.pledges])

    function handleBack() {
        navigate(-1)
    }

    if (!canView) return <Navigate to={`/group/${group.id}`} replace={true} />
    else return (
        <Column.Content className="group-card-details-screen">
            <Column.Header.Start>
                <a onClick={handleBack}>
                    <img src="/icons/arrow-left.svg" alt="back" style={{ width: '1.4rem', height: '1.4rem' }} draggable="false" />
                </a>
            </Column.Header.Start>
            <div className="flex flex-col gap-4">
                {(groupStatus === 'completed' || groupStatus === 'finalized') && <GroupCard group={group} userPledges={userPledges} />}
                {(!!billingAddress) && (
                    <div className="address-cell">
                        <p><strong>Billing Address</strong></p>
                        <p className="flex flex-col items-end">
                            <strong>{billingAddress.line1}</strong>
                            {!!billingAddress.line2 && <span>{billingAddress.line2}</span>}
                            <span className="text-body-2 text-gray-500">{billingAddress.city}, {billingAddress.state}, {billingAddress.postal_code}</span>
                        </p>
                    </div>
                )}
            </div>
            <Column.Footer>
                <Button onClick={handleBack}>Done</Button>
            </Column.Footer>
        </Column.Content>
    )
}

const CardDetailsRoute: RouteObject = {
    path: "card-details",
    element: <GroupCardDetailsScreen />,
}

export default CardDetailsRoute
